import type { EnvironmentalBasis } from '@siliconx/shared';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { BasisTag } from '../BasisTag';
import { formatCarbonMass } from './circularityFormat';
import { CIRCULARITY_GOAL_BAR_CAPTION, CIRCULARITY_GOAL_BAR_TITLE } from './circularityCopy';

/**
 * SIL-473 — avoided-to-date vs. org goal bar. Both figures are SAMPLE (no
 * closed marketplace transactions in prod — see
 * `lib/circularitySampleData.ts`); advisory-vs-advisory is the one pairing
 * the SIL-459 register allows on the same axis, since neither number is a
 * footprint figure.
 */
export function AvoidedGoalBar({
  avoidedToDateKgco2e,
  goalKgco2e,
  basis,
}: {
  avoidedToDateKgco2e: number;
  goalKgco2e: number;
  basis: EnvironmentalBasis;
}) {
  const pct = goalKgco2e > 0 ? Math.min(1, Math.max(0, avoidedToDateKgco2e / goalKgco2e)) : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <SectionLabel>{CIRCULARITY_GOAL_BAR_TITLE}</SectionLabel>
          <BasisTag basis={basis} />
        </div>
        <span className="font-mono text-xs tabular-nums text-sky-900/70">
          {formatCarbonMass(avoidedToDateKgco2e)} / {formatCarbonMass(goalKgco2e)}
        </span>
      </div>

      <div className="h-3 w-full overflow-hidden rounded-full bg-sky-100" role="presentation">
        <div
          className="h-full rounded-full bg-sky-500"
          style={{ width: `${Math.round(pct * 100)}%` }}
          data-testid="circularity-goal-bar-fill"
        />
      </div>

      <p className="text-xs leading-relaxed text-sky-900/60">{CIRCULARITY_GOAL_BAR_CAPTION}</p>
    </div>
  );
}
